import React, { useState, useRef, useContext, useEffect, useCallback, useMemo } from "react";

// ============================================================================
// THIRD PARTY IMPORTS
// ============================================================================
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import moment from "moment";
import Moment from "react-moment";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import { useReactToPrint } from "react-to-print";

// ============================================================================
// LOCAL IMPORTS
// ============================================================================
import axios from "axios";
import { BASE_URL } from "../../../../BaseUrl";
import {
    _t,
    getCookie,
    customStyle,
    formatPrice,
    getSystemSettings
} from "../../../../functions/Functions";
import ReportTypeOptions from "../../../components/Reports/ReportTypeOptions";
import CustomSpinLoader from "../../../components/CustomSpinLoader";
import { UserContext } from "../../../../contexts/User";
import { RestaurantContext } from "../../../../contexts/Restaurant";
import AccountsSidebar from "../accounts/AccountsSidebar";
import { SettingsContext } from "../../../../contexts/Settings";
import { ToastDismiss, ToastError } from "../../../../functions/ToastShow";

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Report type constants
 * - SUMMARY: Shows aggregated data per document
 * - DETAIL: Shows line-by-line breakdown of each document
 */
const REPORT_TYPES = {
    SUMMARY: "summary",
    DETAIL: "detail"
};

/**
 * Grouping options for organizing report data
 * - DOCUMENT_DATE: Group by transaction date
 * - CUSTOMER: Group by customer
 */
const GROUP_BY_TYPES = {
    DOCUMENT_DATE: "document_date",
    CUSTOMER: "customer"
};

/**
 * Receipt method types
 * - BANK: Bank receipt transactions
 * - CASH: Cash receipt transactions
 */
const RECEIPT_TYPES = {
    BANK: "bank_payment",
    CASH: "cash_payment"
};

/**
 * Output format options
 * - EXCEL: Export to Excel file
 * - VIEW: Display in browser
 * - PRINT: Print to PDF/printer
 */
const OUTPUT_OPTIONS = {
    EXCEL: "excel",
    VIEW: "view",
    PRINT: "print"
};

/**
 * Consistent cell styling across all tables
 * Centralized for easy theme customization
 */
const CELL_STYLES = {
    header: "text-capitalize align-middle border-1 border",
    headerStyle: { fontSize: "0.77rem" },
    cell: "text-capitalize align-middle border-1 border sm-text",
    totalCell: "text-capitalize align-middle border-1 border"
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Generates dynamic table headers based on grouping and report type
 * @param {Object} groupBy - Selected grouping option
 * @param {boolean} isDetailReport - Whether this is a detail report
 * @returns {Array} Array of header objects with name and key
 */
const generateTableHeaders = (groupBy, isDetailReport = false, viewOptions) => {
    // Determine if we're grouping by date or customer
    const isGroupedByDate = groupBy?.value === GROUP_BY_TYPES.DOCUMENT_DATE;
    const isPrint = viewOptions === OUTPUT_OPTIONS.PRINT || false;
    // Base headers that appear in all reports
    const baseHeaders = [
        { name: "Branch Name", key: "branch_name" }
    ];

    // Dynamic headers change based on grouping selection
    const groupHeaders = isGroupedByDate
        ? [
            { name: "Document Date", key: "document_date" },
            { name: "Customer", key: "customer_name" }
        ]
        : [
            { name: "Customer", key: "customer_name" },
            { name: "Document Date", key: "document_date" }
        ];

    // Common headers for both report types
    const commonHeaders = [
        { name: "Document No", key: "document_identity" }
    ];

    // Detail reports have additional account-level columns
    const accountHeaders = isDetailReport
        ? [
            { name: "Master Account", key: "transaction_account" },
            { name: "Account", key: "account" },
            { name: "Remarks", key: "remarks" }
        ]
        : [
            { name: "Transaction Account", key: "transaction_account" },
            { name: "Remarks", key: "remarks" }
        ];

    // Amount columns
    const amountHeaders = isDetailReport
        ? [
            { name: "Net Amount", key: "total_net_amount" },
            { name: "Tax", key: "total_other_tax_amount" },
            { name: "WHT", key: "total_wht_amount" },
            { name: "Amount", key: "total_amount" }
        ]
        : [
            { name: "Total Net Amount", key: "total_net_amount" },
            { name: "Total Tax", key: "total_other_tax_amount" },
            { name: "Total WHT", key: "total_wht_amount" },
            { name: "Total Amount", key: "total_amount" }
        ];

    const timstampHeaders = isPrint ? [] : [
        { name: "Created Date", key: "created_at" },
        { name: "Updated Date", key: "updated_at" },
        { name: "Posted Date", key: "posted_at" }
    ]

    return [
        ...baseHeaders,
        ...groupHeaders,
        ...commonHeaders,
        ...accountHeaders,
        ...amountHeaders,
        ...timstampHeaders
    ];
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * ReceiptReport Component
 * Main component for generating and displaying receipt reports
 * Supports both bank and cash receipts with summary/detail views
 */
const ReceiptReport = () => {
    // ========================================================================
    // HOOKS & CONTEXT
    // ========================================================================
    const { t } = useTranslation();
    const { getCustomer, customerForSearch } = useContext(UserContext);

    const { branchForSearch } = useContext(RestaurantContext);
    const { loading, dataPaginating, setDataPaginating, generalSettings } = useContext(SettingsContext);

    // ========================================================================
    // REFS
    // ========================================================================
    const componentRef = useRef(); // For Excel export
    const componentRefThermal = useRef(); // For print/view

    // ========================================================================
    // STATE MANAGEMENT
    // ========================================================================
    const [reportData, setReportData] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [generatedReport, setGeneratedReport] = useState(false);
    const [option, setOption] = useState(OUTPUT_OPTIONS.EXCEL);
    const [viewDetailReport, setViewDetailReport] = useState(false);

    // Consolidated options state
    const [selectedOptions, setSelectedOptions] = useState({
        reportType: REPORT_TYPES.SUMMARY,
        branch: null,
        customer: null,
        report: {
            label: t("Bank Receipt Report"),
            value: RECEIPT_TYPES.BANK
        },
        groupBy: null
    });

    const siteName = useMemo(() => getSystemSettings(generalSettings, "siteName"), [generalSettings]);

    // ========================================================================
    // MEMOIZED VALUES
    // ========================================================================

    /**
     * Available report types (Bank/Cash)
     */
    const REPORT_LIST = useMemo(() => [
        { label: t("Bank Receipt Report"), value: RECEIPT_TYPES.BANK },
        { label: t("Cash Receipt Report"), value: RECEIPT_TYPES.CASH }
    ], [t]);

    /**
     * Grouping options (Date/Customer)
     */
    const GROUP_BY_OPTIONS = useMemo(() => [
        { label: t("Document Date"), value: GROUP_BY_TYPES.DOCUMENT_DATE },
        { label: t("Customer"), value: GROUP_BY_TYPES.CUSTOMER }
    ], [t]);

    /**
     * Report type options (Summary/Detail)
     */
    const REPORT_TYPE_OPTIONS = useMemo(() => [
        { label: t("Summary Report"), value: REPORT_TYPES.SUMMARY, name: "report_type" },
        { label: t("Detail Report"), value: REPORT_TYPES.DETAIL, name: "report_type" }
    ], [t]);

    // ========================================================================
    // EFFECTS
    // ========================================================================

    /**
     * Load supplier data on component mount
     */
    useEffect(() => {
        if (!customerForSearch) {
            getCustomer();
        }
    }, [customerForSearch, getCustomer]);

    // ========================================================================
    // PRINT CONFIGURATION
    // ========================================================================

    /**
     * Configure print settings
     */
    const thermalPrint = useReactToPrint({
        content: () => componentRefThermal.current,
        pageStyle: `
            @page {
                margin: 15px 7px;
            }
        `,
    });

    // ========================================================================
    // EVENT HANDLERS
    // ========================================================================

    /**
     * Handle output option change (Excel/View/Print)
     */
    const handleOption = (e) => {
        const newOption = e.target.value;
        setOption(newOption);

        if (newOption === OUTPUT_OPTIONS.EXCEL || newOption === OUTPUT_OPTIONS.PRINT) {
            setViewDetailReport(false);
        }
    };

    /**
     * Handle filter option changes
     */
    const handleChangeOptions = (type, value) => {

        setSelectedOptions((prev) => ({
            ...prev,
            [type]: value,
        }));

        // Reset report when changing payment type
        if (type === "report" || type === "reportType" || type === "groupBy") {
            setViewDetailReport(false);
            setGeneratedReport(false);
            setReportData(null)
        }
    };

    /**
     * Generate and fetch report data from API
     */
    const handleRequestReport = async (e) => {

        e.preventDefault();

        if (!startDate || !endDate || !selectedOptions?.branch) {
            ToastError({ title: t("Please select from date, to date and branch") });
            return;
        }

        if (!selectedOptions?.groupBy) {
            ToastError({ title: t("Please select report group") });
            return;
        }

        setDataPaginating(true);

        try {
            // Prepare API payload
            const payload = {
                fromDate: moment(startDate).format("YYYY-MM-DD"),
                toDate: moment(endDate).format("YYYY-MM-DD"),
                branch_id: selectedOptions?.branch?.id,
                type: selectedOptions?.reportType,
                customer_id: selectedOptions?.customer?.id,
                groupby: selectedOptions?.groupBy?.value,

            };

            // Determine API endpoint
            const url = selectedOptions?.report?.value === RECEIPT_TYPES.CASH
                ? `${BASE_URL}/settings/cash-receipt-report`
                : `${BASE_URL}/settings/bank-receipt-report`;

            // Fetch report data
            const response = await axios.post(url, payload, {
                headers: { Authorization: `Bearer ${getCookie()}` }
            });

            if (response.status === 200 && response.data) {

                if (Array.isArray(response.data) && response.data.length === 0) {
                    ToastDismiss()
                    ToastError({ title: t("No data found") });
                    setReportData(null);
                    setGeneratedReport(false);
                    return;
                }

                setReportData(response.data);
                setGeneratedReport(true);

                // Process output based on selected option
                if (option === OUTPUT_OPTIONS.EXCEL) {
                    componentRef.current.handleDownload();
                } else if (option === OUTPUT_OPTIONS.VIEW) {
                    setViewDetailReport(true);
                } else if (option === OUTPUT_OPTIONS.PRINT) {
                    thermalPrint();
                }
            } else {
                ToastError({
                    title: t(response.data?.message || "Please try again!")
                });
            }
        } catch (error) {
            console.error("Report generation error:", error);
            ToastError({ title: t("Please try again!") });
        } finally {
            setDataPaginating(false);
        }
    };

    /**
     * Generate report title based on selected options
     */
    const getHeaderTitle = useCallback(() => {
        const isBank = selectedOptions?.report?.value === RECEIPT_TYPES.BANK;
        const typeLabel = selectedOptions?.reportType === REPORT_TYPES.SUMMARY
            ? "Summary"
            : "Detail";
        const receiptType = isBank ? "Bank" : "Cash";

        return t(`${receiptType} Receipt ${typeLabel} Report`);
    }, [selectedOptions?.report?.value, selectedOptions?.reportType, t]);

    // ========================================================================
    // RENDER
    // ========================================================================
    return (
        <>
            <Helmet>
                <title>{_t(t("Receipt Reports"))}</title>
            </Helmet>

            {/* Hidden Excel export button */}
            <ReactHTMLTableToExcel
                id="test-table-xls-button"
                className="download-table-xls-button btn btn-success float-right mr-3 invisible"
                table="receipt-report-to-xls"
                filename={getHeaderTitle()}
                sheet="sheet 1"
                buttonText="Download"
                ref={componentRef}
            />

            <main id="main" data-simplebar>
                <div className="container-fluid">
                    <div className="row t-mt-10 gx-2">
                        {/* ============================================ */}
                        {/* SIDEBAR */}
                        {/* ============================================ */}
                        <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
                            <AccountsSidebar />
                        </div>

                        {/* ============================================ */}
                        {/* MAIN CONTENT AREA */}
                        {/* ============================================ */}
                        <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
                            <div className="t-bg-white">
                                <div className="fk-scroll--pos-menu table-bottom-info-hide" data-simplebar>
                                    <div className="t-pl-15 t-pr-15">
                                        {/* Loading indicator */}
                                        <CustomSpinLoader loading={loading || dataPaginating} />

                                        <div key="smtp-form">
                                            {/* ======================================== */}
                                            {/* HEADER SECTION */}
                                            {/* ======================================== */}
                                            <div className="gx-2 align-items-center t-pt-15 t-pb-15">
                                                <div className="row t-mb-15 mb-md-0">
                                                    {/* Breadcrumb */}
                                                    <ul className="t-list fk-breadcrumb col-6">
                                                        <li className="fk-breadcrumb__list">
                                                            <span className="pl-3 t-link fk-breadcrumb__link text-capitalize">
                                                                {t("Receipt Reports")}
                                                            </span>
                                                        </li>
                                                    </ul>

                                                    {/* Output type selector */}
                                                    <div className="col-6 d-flex justify-content-end">
                                                        <ReportTypeOptions
                                                            selectedOption={option}
                                                            onChange={handleOption}
                                                            view={true}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* ======================================== */}
                                            {/* FILTER ROW 1 */}
                                            {/* ======================================== */}
                                            <div className="row mt-2">
                                                {/* Start Date */}
                                                <div className="col-2">
                                                    <DatePicker
                                                        selected={startDate}
                                                        onChange={(date) => setStartDate(date)}
                                                        peekNextMonth
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                        className="form-control t-mb-15 table-text"
                                                        placeholderText={t("From date")}
                                                        shouldCloseOnSelect={false}
                                                        maxDate={new Date()}
                                                    />
                                                </div>

                                                {/* End Date */}
                                                <div className="col-2">
                                                    <DatePicker
                                                        selected={endDate}
                                                        onChange={(date) => setEndDate(date)}
                                                        peekNextMonth
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                        className="form-control table-text"
                                                        placeholderText={t("To date")}
                                                        shouldCloseOnSelect={false}
                                                        maxDate={new Date()}
                                                    />
                                                </div>

                                                {/* Branch Selector */}
                                                <div className="col-2">
                                                    <Select
                                                        options={branchForSearch || []}
                                                        getOptionLabel={(option) => option.name}
                                                        getOptionValue={(option) => option.id}
                                                        value={selectedOptions?.branch}
                                                        styles={customStyle}
                                                        isClearable
                                                        classNamePrefix="select"
                                                        className="table-text"
                                                        onChange={(val) => handleChangeOptions("branch", val)}
                                                        maxMenuHeight="200px"
                                                        placeholder={t("Select a branch") + ".."}
                                                    />
                                                </div>

                                                {/* Customer Selector (Optional) */}
                                                <div className="col-3">
                                                    <Select
                                                        options={customerForSearch || []}
                                                        getOptionLabel={(option) => option.name}
                                                        getOptionValue={(option) => option.id}
                                                        value={selectedOptions?.customer}
                                                        onChange={(val) => handleChangeOptions("customer", val)}
                                                        classNamePrefix="select"
                                                        className="table-text"
                                                        isClearable
                                                        maxMenuHeight="200px"
                                                        placeholder={t("Please select a customer") + ".."}
                                                        styles={customStyle}
                                                    />
                                                </div>

                                                {/* Receipt Type Selector (Bank/Cash) */}
                                                <div className="col-3">
                                                    <Select
                                                        options={REPORT_LIST}
                                                        value={selectedOptions?.report}
                                                        onChange={(val) => handleChangeOptions("report", val)}
                                                        classNamePrefix="select"
                                                        className="table-text"
                                                        maxMenuHeight="200px"
                                                        placeholder={t("Please select report type") + ".."}
                                                        styles={customStyle}
                                                    />
                                                </div>
                                            </div>

                                            {/* ======================================== */}
                                            {/* FILTER ROW 2 */}
                                            {/* ======================================== */}
                                            <div className="row align-items-center">
                                                {/* Group By Selector */}
                                                <div className="col-3">
                                                    <Select
                                                        options={GROUP_BY_OPTIONS}
                                                        value={selectedOptions?.groupBy}
                                                        onChange={(val) => handleChangeOptions("groupBy", val)}
                                                        classNamePrefix="select"
                                                        className="table-text"
                                                        maxMenuHeight="200px"
                                                        placeholder={t("Please select report group") + ".."}
                                                        styles={customStyle}
                                                    />
                                                </div>

                                                {/* Report Type Radio Buttons */}
                                                <div className="col-7 d-flex flex-wrap justify-content-end">
                                                    {REPORT_TYPE_OPTIONS.map((opt, index) => (
                                                        <div
                                                            key={opt.value}
                                                            className={`d-inline d-flex align-items-center ${index !== REPORT_TYPE_OPTIONS.length - 1 ? "mr-3" : ""
                                                                }`}
                                                        >
                                                            <input
                                                                type="radio"
                                                                id={opt.value}
                                                                name={opt.name}
                                                                value={opt.value}
                                                                checked={selectedOptions?.reportType === opt.value}
                                                                onChange={() => handleChangeOptions("reportType", opt.value)}
                                                                className="form-check-input pointer-cursor"
                                                            />
                                                            <label
                                                                htmlFor={opt.value}
                                                                style={{ fontSize: "16px", borderRadius: "5px" }}
                                                                className="form-radio-label pointer-cursor ml-2 pt-1 text-primary"
                                                            >
                                                                {opt.label}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Generate Report Button */}
                                                <div className="col-2">
                                                    <button
                                                        className="ml-1 btn btn-block btn-primary sm-text py-2 rounded"
                                                        onClick={handleRequestReport}
                                                        disabled={dataPaginating}
                                                    >
                                                        {dataPaginating ? "LOADING..." : "REPORT"}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* ======================================== */}
                                            {/* REPORT DISPLAY AREA */}
                                            {/* ======================================== */}
                                            <ReportPrintView
                                                ref={componentRefThermal}
                                                viewDetailReport={viewDetailReport}
                                                getHeaderTitle={getHeaderTitle}
                                                reportData={reportData}
                                                generatedReport={generatedReport}
                                                branch={selectedOptions?.branch}
                                                startDate={startDate}
                                                endDate={endDate}
                                                siteName={siteName}
                                                selectedGroupBy={selectedOptions?.groupBy}
                                                reportType={selectedOptions?.reportType}
                                                viewOptions={option}
                                                t={t}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default ReceiptReport;

// ============================================================================
// REPORT PRINT VIEW COMPONENT
// ============================================================================

/**
 * ReportPrintView Component
 * Container for displaying/printing reports
 */
const ReportPrintView = React.forwardRef((props, ref) => {

    const {
        getHeaderTitle,
        reportData,
        generatedReport,
        branch,
        startDate,
        endDate,
        siteName,
        selectedGroupBy,
        viewDetailReport,
        reportType,
        viewOptions,
        t,
    } = props;

    if (!reportData || !generatedReport || (!Object.keys(reportData)?.length && !reportData?.length)) {
        return null;
    }

    return (
        <div className={`${viewDetailReport ? "" : "d-none"}`}>
            <div ref={ref}>
                <div className="fk-print container-fluid mt-3">
                    <div
                        style={{ maxHeight: viewDetailReport ? "23rem" : "auto" }}
                        className={viewDetailReport ? "bill_modal_scroll" : ""}
                    >

                        {/* Report Header - Flexbox for View/Print */}
                        {viewDetailReport || viewOptions === "print" && (
                            <ReportHeader
                                siteName={siteName}
                                title={getHeaderTitle()}
                                branchName={branch?.name}
                                startDate={startDate}
                                endDate={endDate}
                                reportType={reportType}
                                isExcelMode={false}
                            />
                        )}

                        {/* Report Table */}
                        <table className="table table-bordered table-hover mt-2" id="receipt-report-to-xls">

                            {/* Report Header - Table for Excel */}
                            {!viewDetailReport && viewOptions !== "print" && (<ReportHeader
                                siteName={siteName}
                                title={getHeaderTitle()}
                                branchName={branch?.name}
                                startDate={startDate}
                                endDate={endDate}
                                reportType={reportType}
                                isExcelMode={true}
                            />)}

                            {reportType === REPORT_TYPES.DETAIL ? (
                                <DetailReportTable
                                    reportData={reportData}
                                    generatedReport={generatedReport}
                                    selectedGroupBy={selectedGroupBy}
                                    viewDetailReport={viewDetailReport}
                                    viewOptions={viewOptions}
                                    t={t}
                                />
                            ) : (
                                <SummaryReportTable
                                    reportData={reportData}
                                    generatedReport={generatedReport}
                                    selectedGroupBy={selectedGroupBy}
                                    viewDetailReport={viewDetailReport}
                                    viewOptions={viewOptions}
                                    t={t}
                                />
                            )}
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
});

// ============================================================================
// REPORT HEADER COMPONENT
// ============================================================================

/**
 * ReportHeader Component
 * Displays report title, branch name, and date range
 * Uses flexbox div for view/print, table structure for Excel
 */
const ReportHeader = ({ siteName, title, branchName, startDate, endDate, reportType, isExcelMode }) => {
    const colspan = reportType === REPORT_TYPES.DETAIL ? 14 : 13;

    if (isExcelMode) {
        return (
            <>
                {siteName && (
                    <tr>
                        <th colSpan={colspan} className="text-center xslg-text" >
                            {siteName}
                        </th>
                    </tr>
                )}
                <tr><td colSpan={colspan}></td></tr>
                <tr>
                    <th colSpan={colspan} className="text-center xslg-text">
                        {title}
                    </th>
                </tr>
                <tr><td colSpan={colspan}></td></tr>
                {branchName && (
                    <>
                        <tr>
                            <th colSpan={colspan} className="text-center">
                                {branchName}
                            </th>
                        </tr>
                        <tr><td colSpan={colspan}></td></tr>
                    </>
                )}
                <tr>
                    <th colSpan={colspan} className="text-center">
                        FROM <Moment format="LL">{startDate}</Moment>
                        &nbsp;TO&nbsp;
                        <Moment format="LL">{endDate}</Moment>
                    </th>
                </tr>
                <tr>
                    <td colSpan={colspan}></td>
                </tr>
            </>
        )
    }

    return (
        <div className="mb-3" style={{ textAlign: "center" }}>
            {siteName && (
                <h3 className="xslg-text my-2">{siteName}</h3>
            )}
            <h3 className="xslg-text my-2">{title}</h3>
            {branchName && (
                <h4 className="my-2" style={{ fontSize: "1rem" }}>{branchName}</h4>
            )}
            <p className="my-2" style={{ fontSize: "0.9rem" }}>
                FROM <Moment format="LL">{startDate}</Moment>
                &nbsp;TO&nbsp;
                <Moment format="LL">{endDate}</Moment>
            </p>
        </div>
    );


};

// ============================================================================
// TABLE HEADER COMPONENT
// ============================================================================

/**
 * TableHeader Component
 * Renders table column headers
 */
const TableHeader = ({ headers, viewDetailReport, t }) => (
    <thead className="align-middle">
        <tr style={viewDetailReport ? { position: 'sticky', top: '-0.1px', backgroundColor: 'white' } : {}}>
            {headers.map((header, index) => (
                <th key={index} className={CELL_STYLES.header} style={CELL_STYLES.headerStyle}>
                    {t(header.name)}
                </th>
            ))}
        </tr>
    </thead>
);

// ============================================================================
// HELPER COMPONENTS FOR TABLE RENDERING
// ============================================================================

/**
 * TimestampColumns Component
 * Renders timestamp columns (Created, Updated, Posted) conditionally based on viewOptions
 * Hidden in print mode for cleaner output
 */
const TimestampColumns = ({ item, viewOptions }) => {
    if (viewOptions === OUTPUT_OPTIONS.PRINT) return null;

    return (
        <>
            <td className={CELL_STYLES.cell}>
                <Moment format="LL">{item.created_at}</Moment>
            </td>
            <td className={CELL_STYLES.cell}>
                <Moment format="LL">{item.updated_at}</Moment>
            </td>
            <td className={CELL_STYLES.cell}>
                {item.post_date ? <Moment format="LL">{item.post_date}</Moment> : null}
            </td>
        </>
    );
};

/**
 * GroupTotalRow Component
 * Renders group subtotal row with consistent styling
 * @param {number} colSpan - Number of columns to span before amount columns
 */
const GroupTotalRow = ({ groupNetAmount, groupOtherTax, groupWhtTax, groupAmount, colSpan }) => (
    <>
        <tr className="fw-bold">
            <td></td>
            <th className={CELL_STYLES.header} style={CELL_STYLES.headerStyle}>
                <strong>Total</strong>
            </th>
            <td colSpan={colSpan}></td>
            <td className={CELL_STYLES.cell}>
                <strong>{formatPrice(groupNetAmount)}</strong>
            </td>
            <td className={CELL_STYLES.cell}>
                <strong>{formatPrice(groupOtherTax)}</strong>
            </td>
            <td className={CELL_STYLES.cell}>
                <strong>{formatPrice(groupWhtTax)}</strong>
            </td>
            <td className={CELL_STYLES.cell}>
                <strong>{formatPrice(groupAmount)}</strong>
            </td>
        </tr>
        <tr></tr>
    </>
);

/**
 * AmountColumns Component
 * Renders the four amount columns with consistent formatting
 */
const AmountColumns = ({ netAmount, otherTax, whtTax, amount }) => (
    <>
        <td className={CELL_STYLES.cell}>{formatPrice(netAmount)}</td>
        <td className={CELL_STYLES.cell}>{formatPrice(otherTax)}</td>
        <td className={CELL_STYLES.cell}>{formatPrice(whtTax)}</td>
        <td className={CELL_STYLES.cell}>{formatPrice(amount)}</td>
    </>
);

// ============================================================================
// SUMMARY REPORT TABLE COMPONENT
// ============================================================================

/**
 * SummaryReportTable Component
 * Displays aggregated receipt data
 */
const SummaryReportTable = ({ reportData, generatedReport, selectedGroupBy, viewDetailReport, viewOptions, t }) => {

    const headers = useMemo(
        () => generateTableHeaders(selectedGroupBy, false, viewOptions),
        [selectedGroupBy, viewOptions]
    );

    const isGroupedByDate = selectedGroupBy?.value === GROUP_BY_TYPES.DOCUMENT_DATE;

    // Grand totals
    let grandNetAmount = 0;
    let grandAmount = 0;
    let grandOtherTax = 0;
    let grandWhtTax = 0;

    return (
        <>
            <TableHeader headers={headers} viewDetailReport={viewDetailReport} t={t} />
            <tbody className="align-middle">
                {generatedReport && reportData && Object.entries(reportData).map(([branchName, nestedGroups], branchIndex) => (
                    <React.Fragment key={branchIndex}>
                        {Object.entries(nestedGroups).map(([groupKey, items], groupIndex) => {
                            // Group totals for this specific group
                            let groupNetAmount = 0;
                            let groupOtherTax = 0;
                            let groupWhtTax = 0;
                            let groupAmount = 0;

                            return (
                                <React.Fragment key={groupIndex}>
                                    {items.map((item, itemIndex) => {
                                        // Accumulate grand totals
                                        grandNetAmount += parseFloat(item.total_net_amount || 0);
                                        grandOtherTax += parseFloat(item.total_other_tax_amount || 0);
                                        grandWhtTax += parseFloat(item.total_wht_amount || 0);
                                        grandAmount += parseFloat(item.total_amount || 0);

                                        // Accumulate group totals
                                        groupNetAmount += parseFloat(item.total_net_amount || 0);
                                        groupOtherTax += parseFloat(item.total_other_tax_amount || 0);
                                        groupWhtTax += parseFloat(item.total_wht_amount || 0);
                                        groupAmount += parseFloat(item.total_amount || 0);

                                        const isFirstInBranch = groupIndex === 0 && itemIndex === 0;
                                        const isFirstInGroup = itemIndex === 0;

                                        return (
                                            <tr key={itemIndex}>
                                                {/* Branch Name */}
                                                {isFirstInBranch ? (
                                                    <th className={CELL_STYLES.header} style={CELL_STYLES.headerStyle}>
                                                        {branchName}
                                                    </th>
                                                ) : (
                                                    <td className={CELL_STYLES.cell}></td>
                                                )}

                                                {/* First grouped column */}
                                                {isFirstInGroup ? (
                                                    <th className={CELL_STYLES.header} style={CELL_STYLES.headerStyle}>
                                                        {isGroupedByDate ? (
                                                            <Moment format="LL">{item.document_date}</Moment>
                                                        ) : (
                                                            item.customer_name
                                                        )}
                                                    </th>
                                                ) : (
                                                    <td className={CELL_STYLES.cell}></td>
                                                )}

                                                {/* Second grouped column */}
                                                <td className={CELL_STYLES.cell}>
                                                    {isGroupedByDate ? (
                                                        item.customer_name
                                                    ) : (
                                                        <Moment format="LL">{item.document_date}</Moment>
                                                    )}
                                                </td>

                                                {/* Data columns */}
                                                <td className={CELL_STYLES.cell}>{item.document_identity || 'N/A'}</td>
                                                <td className={CELL_STYLES.cell}>{item.master_transaction_name || 'N/A'}</td>
                                                <td className={CELL_STYLES.cell}>{item.remarks || 'N/A'}</td>

                                                {/* Amount columns */}
                                                <AmountColumns
                                                    netAmount={item.total_net_amount}
                                                    otherTax={item.total_other_tax_amount}
                                                    whtTax={item.total_wht_amount}
                                                    amount={item.total_amount}
                                                />

                                                {/* Timestamp columns */}
                                                <TimestampColumns item={item} viewOptions={viewOptions} />
                                            </tr>
                                        );
                                    })}

                                    {/* Group Total Row */}
                                    <GroupTotalRow
                                        groupNetAmount={groupNetAmount}
                                        groupOtherTax={groupOtherTax}
                                        groupWhtTax={groupWhtTax}
                                        groupAmount={groupAmount}
                                        colSpan={4}
                                    />
                                </React.Fragment>
                            );
                        })}
                    </React.Fragment>
                ))}

                {/* Spacer row */}
                <tr></tr>

                {/* Grand Total Row */}
                <tr style={{ background: "#f8f9fa" }}>
                    <td colSpan="5"></td>
                    <td className={`text-end ${CELL_STYLES.totalCell}`} style={CELL_STYLES.headerStyle}>
                        <strong>Grand Total</strong>
                    </td>
                    <td className={CELL_STYLES.totalCell} style={CELL_STYLES.headerStyle}>
                        <strong>{formatPrice(grandNetAmount)}</strong>
                    </td>
                    <td className={CELL_STYLES.totalCell} style={CELL_STYLES.headerStyle}>
                        <strong>{formatPrice(grandOtherTax)}</strong>
                    </td>
                    <td className={CELL_STYLES.totalCell} style={CELL_STYLES.headerStyle}>
                        <strong>{formatPrice(grandWhtTax)}</strong>
                    </td>
                    <td className={CELL_STYLES.totalCell} style={CELL_STYLES.headerStyle}>
                        <strong>{formatPrice(grandAmount)}</strong>
                    </td>
                </tr>
            </tbody>
        </>
    );
};

// ============================================================================
// DETAIL REPORT TABLE COMPONENT
// ============================================================================

/**
 * DetailReportTable Component
 * Displays line-by-line breakdown of receipt details
 */
const DetailReportTable = ({ reportData, generatedReport, selectedGroupBy, viewDetailReport, viewOptions, t }) => {
    const headers = useMemo(
        () => generateTableHeaders(selectedGroupBy, true, viewOptions),
        [selectedGroupBy, viewOptions]
    );

    const isGroupedByDate = selectedGroupBy?.value === GROUP_BY_TYPES.DOCUMENT_DATE;

    // Grand totals
    let grandNetAmount = 0;
    let grandOtherTax = 0;
    let grandWhtTax = 0;
    let grandAmount = 0;

    return (
        <>
            <TableHeader headers={headers} viewDetailReport={viewDetailReport} t={t} />
            <tbody className="align-middle">
                {generatedReport && reportData && Object.entries(reportData).map(([branchName, nestedGroups], branchIndex) => (
                    <React.Fragment key={branchIndex}>
                        {Object.entries(nestedGroups).map(([groupKey, items], groupIndex) => {
                            // Group totals for this specific group
                            let groupNetAmount = 0;
                            let groupOtherTax = 0;
                            let groupWhtTax = 0;
                            let groupAmount = 0;

                            return (
                                <React.Fragment key={groupIndex}>
                                    {items.map((item, itemIndex) => {
                                        if (item?.details?.length === 0) {
                                            console.warn("No receipt details found for item:", item);
                                            return null;
                                        }

                                        return (
                                            <React.Fragment key={`${groupIndex}-${itemIndex}`}>
                                                {item.details.map((detail, detailIndex) => {
                                                    // Accumulate grand totals
                                                    grandAmount += parseFloat(detail.amount || 0);
                                                    grandNetAmount += parseFloat(detail.net_amount || 0);
                                                    grandOtherTax += parseFloat(detail.other_tax_amount || 0);
                                                    grandWhtTax += parseFloat(detail.wht_amount || 0);

                                                    // Accumulate group totals
                                                    groupAmount += parseFloat(detail.amount || 0);
                                                    groupNetAmount += parseFloat(detail.net_amount || 0);
                                                    groupOtherTax += parseFloat(detail.other_tax_amount || 0);
                                                    groupWhtTax += parseFloat(detail.wht_amount || 0);

                                                    const isFirstInBranch = groupIndex === 0 && itemIndex === 0 && detailIndex === 0;
                                                    const isFirstInGroup = itemIndex === 0 && detailIndex === 0;

                                                    return (
                                                        <tr key={`${groupIndex}-${itemIndex}-${detailIndex}`}>
                                                            {/* Branch Name */}
                                                            {isFirstInBranch ? (
                                                                <th className={CELL_STYLES.header} style={CELL_STYLES.headerStyle}>
                                                                    {branchName}
                                                                </th>
                                                            ) : (
                                                                <td className={CELL_STYLES.cell}></td>
                                                            )}

                                                            {/* First grouped column */}
                                                            {isFirstInGroup ? (
                                                                <th className={CELL_STYLES.header} style={CELL_STYLES.headerStyle}>
                                                                    {isGroupedByDate ? (
                                                                        <Moment format="LL">{item.document_date}</Moment>
                                                                    ) : (
                                                                        item.customer_name
                                                                    )}
                                                                </th>
                                                            ) : (
                                                                <td className={CELL_STYLES.cell}></td>
                                                            )}

                                                            {/* Second grouped column */}
                                                            <td className={CELL_STYLES.cell}>
                                                                {isGroupedByDate ? (
                                                                    item.customer_name
                                                                ) : (
                                                                    <Moment format="LL">{item.document_date}</Moment>
                                                                )}
                                                            </td>


                                                            <td className={CELL_STYLES.cell}>{item.document_identity || 'N/A'}</td>

                                                            {/* Master Account */}
                                                            <td className={CELL_STYLES.cell}>{item.master_transaction_name || 'N/A'}</td>

                                                            {/* Transaction Account (Detail) */}
                                                            <td className={CELL_STYLES.cell}>{detail.level3_name || 'N/A'}</td>

                                                            {/* Remarks */}
                                                            <td className={CELL_STYLES.cell}>{detail.detail_remarks || 'N/A'}</td>

                                                            {/* Amount columns */}
                                                            <AmountColumns
                                                                netAmount={detail.net_amount}
                                                                otherTax={detail.other_tax_amount}
                                                                whtTax={detail.wht_amount}
                                                                amount={detail.amount}
                                                            />

                                                            {/* Timestamp columns */}
                                                            <TimestampColumns item={item} viewOptions={viewOptions} />
                                                        </tr>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}

                                    {/* Group Total Row */}
                                    <GroupTotalRow
                                        groupNetAmount={groupNetAmount}
                                        groupOtherTax={groupOtherTax}
                                        groupWhtTax={groupWhtTax}
                                        groupAmount={groupAmount}
                                        colSpan={5}
                                    />
                                </React.Fragment>
                            );
                        })}
                    </React.Fragment>
                ))}

                {/* Spacer row */}
                <tr></tr>

                {/* Grand Total Row */}
                <tr style={{ background: "#f8f9fa" }}>
                    <td colSpan="6"></td>
                    <td className={`text-end ${CELL_STYLES.totalCell}`} style={CELL_STYLES.headerStyle}>
                        <strong>Grand Total</strong>
                    </td>
                    <td className={CELL_STYLES.totalCell} style={CELL_STYLES.headerStyle}>
                        <strong>{formatPrice(grandNetAmount)}</strong>
                    </td>
                    <td className={CELL_STYLES.totalCell} style={CELL_STYLES.headerStyle}>
                        <strong>{formatPrice(grandOtherTax)}</strong>
                    </td>
                    <td className={CELL_STYLES.totalCell} style={CELL_STYLES.headerStyle}>
                        <strong>{formatPrice(grandWhtTax)}</strong>
                    </td>
                    <td className={CELL_STYLES.totalCell} style={CELL_STYLES.headerStyle}>
                        <strong>{formatPrice(grandAmount)}</strong>
                    </td>
                </tr>
            </tbody>
        </>
    );
};
